import { supabase } from '../../lib/supabase';
import { Recording } from './types';

/**
 * Service for database operations related to recordings
 */
export class DatabaseService {
  /**
   * Get all recordings for the current user
   */
  async getUserRecordings(userId?: string): Promise<Recording[]> {
    try {
      // Get the current user's ID if not provided
      let currentUserId = userId;
      
      if (!currentUserId) {
        const { data: { user } } = await supabase.auth.getUser();
        currentUserId = user?.id;
        
        if (!currentUserId) {
          // User is not authenticated yet, but we're in a protected route
          // so authentication will complete soon - return empty array
          console.log('DatabaseService: No user ID available yet, returning empty recordings array');
          return [];
        }
      }
      
      const { data, error } = await supabase
        .from('recordings')
        .select(`
          *,
          song:songs (
            title,
            artist,
            audio_url,
            lead_vocal_url,
            cover_url
          ),
          tournament:tournaments (
            title
          )
        `)
        .eq('user_id', currentUserId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting user recordings:', error);
      throw error;
    }
  }

  /**
   * Get a shared recording by ID
   */
  async getSharedRecording(id: string): Promise<Recording | null> {
    try {
      const { data, error } = await supabase
        .from('recordings')
        .select(`
          *,
          song:songs (
            title,
            artist,
            audio_url,
            lead_vocal_url,
            cover_url
          )
        `)
        .eq('id', id)
        .eq('shared', true)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
      }
      return data;
    } catch (error) {
      console.error('Error getting shared recording:', error);
      throw error;
    }
  }

  /**
   * Create a new recording
   */
  async createRecording(recording: Partial<Recording>): Promise<Recording> {
    try {
      const { data, error } = await supabase
        .from('recordings')
        .insert([recording])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating recording:', error);
      throw error;
    }
  }

  /**
   * Update a recording's title
   */
  async updateRecordingTitle(id: string, title: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('recordings')
        .update({ title })
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating recording title:', error);
      throw error;
    }
  }

  /**
   * Share a recording
   */
  async shareRecording(id: string): Promise<string> {
    try {
      const { error: updateError } = await supabase
        .from('recordings')
        .update({ 
          shared: true,
          shared_at: new Date().toISOString()
        })
        .eq('id', id);

      if (updateError) {
        console.error('Error updating recording share status:', updateError);
        throw new Error('Failed to share recording');
      }

      // Generate share URL
      return `${window.location.origin}/shared/${id}`;
    } catch (error) {
      console.error('Error sharing recording:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Unable to share recording at this time');
    }
  }

  /**
   * Delete a recording
   */
  async deleteRecordingFromDB(id: string): Promise<void> {
    try {
      const { data: performances } = await supabase
        .from('performances')
        .select('id')
        .eq('recording_id', id);

      if (performances && performances.length > 0) {
        const performanceIds = performances.map(p => p.id);

        const { error: analysisError } = await supabase
          .from('performance_analysis')
          .delete()
          .in('performance_id', performanceIds);

        if (analysisError) {
          console.error('Error deleting performance analysis:', analysisError);
        }

        const { error: performanceError } = await supabase
          .from('performances')
          .delete()
          .eq('recording_id', id);

        if (performanceError) {
          console.error('Error deleting performances:', performanceError);
          throw performanceError;
        }
      }

      const { error: deleteError } = await supabase
        .from('recordings')
        .delete()
        .eq('id', id);

      if (deleteError) {
        console.error('Error deleting database record:', deleteError);
        throw deleteError;
      }
    } catch (error) {
      console.error('Error deleting recording from database:', error);
      throw error;
    }
  }

  /**
   * Get song title by ID
   */
  async getSongTitle(songId: string): Promise<string> {
    try {
      const { data, error } = await supabase
        .from('songs')
        .select('title')
        .eq('id', songId)
        .single();

      if (error) throw error;
      return data?.title || 'Untitled';
    } catch (error) {
      console.error('Error getting song title:', error);
      return 'Untitled';
    }
  }

  async userHasRecordings(userId: string): Promise<boolean> {
    const { count: recCount } = await supabase
      .from('recordings')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);
    if ((recCount || 0) > 0) return true;
    const { count: perfCount } = await supabase
      .from('performances')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);
    return (perfCount || 0) > 0;
  }
}